import { ConfirmacaoPacoteGiro } from "@/components/ConfirmacaoPacoteGiro";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthProvider";
import { PacotePromocionalDto, SorteioGiroDto } from "@/dtos/BuscarSorteioVigenteDto";
import { labelPlural } from "@/utils/copyTituloBilhete";
import { setSelecaoGiroCheckout } from "@/utils/selecaoGiroCheckout";
import {
    aplicarSelecaoPorQuantidade,
    calcularNudgePacoteGiro,
    criarSelecaoPacote,
    criarSelecaoQuantidade,
} from "@/utils/selecaoPacoteGiro";
import { Minus, Plus } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Seleção da Compra Rápida do Giro — contrato para o fluxo de checkout.
 *
 * - `pacote`: compra pelo `IdPacotePromocional` (preço e qtd do pacote).
 * - `quantidade`: compra avulsa por quantidade × `sorteioGiro.Valor`.
 */
export type SelecaoCompraGiro =
    | {
          modo: "pacote";
          idPacotePromocional: number;
          quantidade: number;
          valor: number;
      }
    | {
          modo: "quantidade";
          quantidade: number;
          valor: number;
      };

interface CompraRapidaGiroProps {
    sorteioGiro: SorteioGiroDto;
    onInteract?: () => void;
    onSelecaoChange?: (selecao: SelecaoCompraGiro) => void;
}

export function CompraRapidaGiro({
    sorteioGiro,
    onInteract,
    onSelecaoChange,
}: CompraRapidaGiroProps) {
    const navigate = useNavigate();
    const { isAuthorized } = useAuth();
    const qtdMinima = sorteioGiro.QtdMinima || 1;
    const valorUnitario = sorteioGiro.Valor || 0;
    const pacotes = sorteioGiro.PacotesPromocionais ?? [];
    const vendaBloqueada = sorteioGiro.VendaBloqueada;

    const [quantidade, setQuantidade] = useState(qtdMinima);
    const [quantidadeAvulsaAnterior, setQuantidadeAvulsaAnterior] = useState(qtdMinima);
    const [pacoteSelecionado, setPacoteSelecionado] = useState<PacotePromocionalDto | null>(
        null
    );

    function notificarInteracao() {
        onInteract?.();
    }

    function emitirSelecao(proxima: SelecaoCompraGiro) {
        onSelecaoChange?.(proxima);
    }

    function aplicarSelecao(
        novaSelecao: SelecaoCompraGiro,
        novaQtd: number,
        pacote: PacotePromocionalDto | null
    ) {
        setQuantidade(novaQtd);
        setPacoteSelecionado(pacote);
        emitirSelecao(novaSelecao);
    }

    function lembrarQuantidadeAvulsa() {
        if (!pacoteSelecionado) {
            setQuantidadeAvulsaAnterior(quantidade);
        }
    }

    function selecionarPorQuantidade(novaQtd: number) {
        lembrarQuantidadeAvulsa();
        const resultado = aplicarSelecaoPorQuantidade(
            novaQtd,
            qtdMinima,
            valorUnitario,
            pacotes
        );
        aplicarSelecao(resultado.selecao, resultado.quantidade, resultado.pacote);
    }

    function handleDecrementar() {
        notificarInteracao();
        if (quantidade <= qtdMinima) {
            return;
        }
        selecionarPorQuantidade(quantidade - 1);
    }

    function handleIncrementar() {
        notificarInteracao();
        selecionarPorQuantidade(quantidade + 1);
    }

    function handleSelecionarPacote(pacote: PacotePromocionalDto) {
        notificarInteracao();
        lembrarQuantidadeAvulsa();
        aplicarSelecao(criarSelecaoPacote(pacote), pacote.Qtd, pacote);
    }

    function handleAlterarSelecao() {
        notificarInteracao();
        const qtd = Math.max(qtdMinima, quantidadeAvulsaAnterior);
        aplicarSelecao(criarSelecaoQuantidade(qtd, valorUnitario), qtd, null);
    }

    function handleComprar() {
        if (vendaBloqueada) {
            return;
        }
        if (isAuthorized) {
            setSelecaoGiroCheckout(selecaoAtual);
            navigate("/compra/finalizar/giro");
            return;
        }
        navigate("/compra/rapida/giro", { state: { selecao: selecaoAtual } });
    }

    function handleNudgeClick() {
        if (!nudge) {
            return;
        }
        handleSelecionarPacote(nudge.pacote);
    }

    const selecaoAtual: SelecaoCompraGiro = pacoteSelecionado
        ? criarSelecaoPacote(pacoteSelecionado)
        : {
              modo: "quantidade",
              quantidade,
              valor: valorUnitario * quantidade,
          };

    const nudge =
        selecaoAtual.modo === "quantidade"
            ? calcularNudgePacoteGiro(quantidade, valorUnitario, pacotes)
            : null;

    if (pacoteSelecionado) {
        return (
            <div className="flex flex-col items-center justify-center gap-4 m-1">
                <ConfirmacaoPacoteGiro
                    pacote={pacoteSelecionado}
                    ctaLabel="Comprar"
                    disabled={vendaBloqueada}
                    disabledLabel="Indisponível"
                    ctaVariant="default"
                    onContinuar={handleComprar}
                    onAlterarSelecao={handleAlterarSelecao}
                />
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center justify-center gap-4 m-1">
            <h1 className="text-center text-primary font-semibold text-xl">
                Compra Rápida
            </h1>

            {pacotes.length > 0 && (
                <div className="flex flex-wrap items-center justify-center gap-2 px-2">
                    {pacotes.map((pacote) => (
                        <Button
                            key={pacote.IdPacotePromocional}
                            type="button"
                            variant="outline"
                            onClick={() => handleSelecionarPacote(pacote)}
                        >
                            {pacote.Qtd} {labelPlural()}
                            {pacote.Valor > 0 && (
                                <span className="ml-1 text-xs opacity-90">
                                    {pacote.Valor.toLocaleString("pt-br", {
                                        style: "currency",
                                        currency: "BRL",
                                    })}
                                </span>
                            )}
                        </Button>
                    ))}
                </div>
            )}

            <div className="flex items-center justify-center gap-3">
                <Button
                    type="button"
                    disabled={quantidade <= qtdMinima}
                    onClick={handleDecrementar}
                    size="lg"
                    variant="outline"
                >
                    <Minus size={20} />
                </Button>
                <span className="flex flex-col select-none justify-center items-center text-primary text-sm font-semibold">
                    <span>{quantidade}</span>
                    <span>{labelPlural()}</span>
                </span>
                <Button type="button" onClick={handleIncrementar} size="lg" variant="outline">
                    <Plus size={20} />
                </Button>
            </div>

            {nudge && (
                <button
                    type="button"
                    onClick={handleNudgeClick}
                    className="text-sm text-primary underline hover:no-underline text-center px-2"
                >
                    {nudge.mensagem}
                </button>
            )}

            {selecaoAtual.valor > 0 && (
                <p className="text-sm text-muted-foreground">
                    Total:{" "}
                    {selecaoAtual.valor.toLocaleString("pt-br", {
                        style: "currency",
                        currency: "BRL",
                    })}
                </p>
            )}

            <div className="flex justify-center items-center">
                <Button
                    className="w-40 items-center justify-center"
                    disabled={vendaBloqueada}
                    onClick={handleComprar}
                    title={
                        vendaBloqueada
                            ? "Venda indisponível"
                            : `Compra por quantidade: ${selecaoAtual.quantidade}`
                    }
                >
                    {vendaBloqueada ? "Indisponível" : "Comprar"}
                </Button>
            </div>
        </div>
    );
}
